import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../firebase/config';
import { getUserProfile } from './user.service';
import { UserProfile } from './user.type';

export type UserUpdateFunction = (callback: (user: UserProfile | null) => void) => () => void;

export const userUpdate: UserUpdateFunction = (callback: (user: UserProfile | null) => void) => {
  // Store the callback globally for manual signOut fallback
  if (typeof window !== 'undefined') {
    (window as any).__authCallback = callback;
  }

  let hasInitialized = false;
  let unsubscribe: (() => void) | null = null;
  let fallbackTimeout: NodeJS.Timeout | null = null;

  // Wait for Firebase to fully initialize then set up listener
  const initializeAuth = async () => {

    // Wait a bit for Firebase to initialize
    await new Promise(resolve => setTimeout(resolve, 1000));

    unsubscribe = onAuthStateChanged(auth, async (firebaseUser: User | null) => {
      hasInitialized = true;

      // Clear fallback timeout since auth state changed
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
        fallbackTimeout = null;
      }

      if (firebaseUser) {
        try {
          const userProfile = await getUserProfile(firebaseUser.uid);
          callback(userProfile);
        } catch (error) {
          callback(null);
        }
      } else {
        callback(null);
      }
    });

    // Fallback timeout in case onAuthStateChanged never fires
    fallbackTimeout = setTimeout(() => {
      if (!hasInitialized) {
        hasInitialized = true;
        callback(null);
      }
      fallbackTimeout = null;
    }, 3000);
  };

  // Start the initialization
  initializeAuth();

  // Return cleanup function
  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
    if (fallbackTimeout) {
      clearTimeout(fallbackTimeout);
      fallbackTimeout = null;
    }
  };
};

export type SignOutFunction = () => Promise<void>;

export const signOut: SignOutFunction = async () => {
  await auth.signOut();

  sessionStorage.removeItem('currentJobRoleId');

  // Clear the stored user role after a short delay to allow redirect to complete
  setTimeout(() => {
    sessionStorage.removeItem('lastUserRole');
  }, 1000);

  setTimeout(() => {
    if (!auth.currentUser) {
      if (typeof window !== 'undefined' && (window as any).__authCallback) {
        (window as any).__authCallback(null);
      }
    }
  }, 100);
};
